import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Card, Group, Select, Stack, Switch, Text } from '@mantine/core'
import * as Sentry from '@sentry/react'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useBranchQuery, useUpdateBranchMutation } from '../../../../api/branches/api'
import { DayOfWeek, DAYS_OF_WEEK, TIME_SLOTS } from '../../../../constants/constants'
import { useUser } from '../../../../user-provider/user-context'

const businessHoursSchema = z
  .record(
    z.enum(DAYS_OF_WEEK),
    // NOTE: we want all keys represented, but zod3 enum record keys are optional (not true for future zod4).
    // To validate that all keys are represented, we use a custom refine function below.
    z
      .object({
        open: z
          .number() // index in TIME_SLOTS
          .min(0)
          .max(TIME_SLOTS.length - 1)
          .nullable(),
        close: z
          .number() // index in TIME_SLOTS
          .min(0)
          .max(TIME_SLOTS.length - 1)
          .nullable(),
        is_working_day: z.boolean(),
      })
      .refine(
        (obj) => {
          if (obj.is_working_day) {
            if (obj.close === null) {
              return false
            }
          }
          return true
        },
        {
          message: 'Close time is required for working days',
        },
      )
      .refine(
        (obj) => {
          if (obj.is_working_day) {
            if (obj.open === null) {
              return false
            }
          }
          return true
        },
        {
          message: 'Open time is required for working days',
        },
      )
      .refine(
        (obj) => {
          if (obj.is_working_day) {
            if (obj.open !== null && obj.close !== null) {
              if (obj.open >= obj.close) {
                return false
              }
            }
          }
          return true
        },
        {
          message: 'Open must be before close',
        },
      ),
  )
  .refine((obj) => DAYS_OF_WEEK.every((key) => !!obj[key]), {
    message: 'All days of the week must be included',
  })

type BusinessHoursFormData = z.infer<typeof businessHoursSchema>

const businessHoursDefaultValues: BusinessHoursFormData = {
  Monday: { open: null, close: null, is_working_day: false },
  Tuesday: { open: null, close: null, is_working_day: false },
  Wednesday: { open: null, close: null, is_working_day: false },
  Thursday: { open: null, close: null, is_working_day: false },
  Friday: { open: null, close: null, is_working_day: false },
  Saturday: { open: null, close: null, is_working_day: false },
  Sunday: { open: null, close: null, is_working_day: false },
}

export const Route = createFileRoute('/app/settings/branch-settings/business-hours')({
  component: BusinessHoursComponent,
})

const getTimeSlotIndex = (timeValue: unknown): number | null => {
  if (!timeValue) return null
  if (typeof timeValue !== 'string') {
    Sentry.captureException(new Error(`Invalid branch time value: ${timeValue}`))
    return null
  }
  const out = TIME_SLOTS.findIndex((slot) => slot.value === timeValue || slot.isoValue === timeValue)
  if (out < 0) {
    Sentry.captureException(new Error(`Invalid branch time value: ${timeValue}`))
    return null
  }
  return out
}

const getTimeSlotValue = (index: number | null | undefined): string | null => {
  if (index === null || index === undefined || index < 0 || index >= TIME_SLOTS.length) return null
  return TIME_SLOTS[index].value
}

function BusinessHoursComponent() {
  const { branchInfo } = useUser()
  const branchId = branchInfo?.provider_branch_id || ''
  const { data: branch } = useBranchQuery(branchId)
  const [hoursFromBackend, setHoursFromBackend] = useState<BusinessHoursFormData>(businessHoursDefaultValues)
  const updateBranchMutation = useUpdateBranchMutation()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    trigger,
  } = useForm<BusinessHoursFormData>({
    resolver: zodResolver(businessHoursSchema),
    defaultValues: businessHoursDefaultValues,
  })

  useEffect(() => {
    if (branch) {
      const hours: Record<string, { open: number | null; close: number | null; is_working_day: boolean }> = {}
      DAYS_OF_WEEK.forEach((day) => {
        const dayHours = branch.working_hours?.[day] || { open: null, close: null, is_working_day: false }
        hours[day] = {
          // These are `any` type coming from the backend, so be careful
          open: getTimeSlotIndex(dayHours.open),
          close: getTimeSlotIndex(dayHours.close),
          is_working_day: !!dayHours.is_working_day,
        }
      })
      setHoursFromBackend(hours)
      reset({ ...hours })
    }
  }, [branch, reset])

  const handleWorkingDayToggle = (day: DayOfWeek, isWorking: boolean) => {
    const currentHours = getValues(`${day}`)
    setValue(`${day}.is_working_day`, isWorking)

    if (isWorking) {
      // Return to whatever the backend had stored
      if (currentHours?.open === null) {
        setValue(`${day}.open`, getTimeSlotIndex(hoursFromBackend[day]?.open))
      }
      if (currentHours?.close === null) {
        setValue(`${day}.close`, getTimeSlotIndex(hoursFromBackend[day]?.close))
      }
    } else {
      // Clear times for non-working days
      setValue(`${day}.open`, null)
      setValue(`${day}.close`, null)
    }

    // Trigger form validation to re-check error state
    trigger(day)
  }

  const onSubmit = (data: BusinessHoursFormData) => {
    if (branchId) {
      // Convert indices back to time values for submission
      const businessHoursForSubmission = Object.fromEntries(
        Object.entries(data).map(([day, hours]) => {
          return [
            day,
            {
              open: getTimeSlotValue(hours.open),
              close: getTimeSlotValue(hours.close),
              is_working_day: hours.is_working_day,
            },
          ]
        }),
      ) as Record<DayOfWeek, { open: string | null; close: string | null; is_working_day: boolean }>

      updateBranchMutation.mutate({
        branchId,
        data: {
          working_hours: businessHoursForSubmission,
        },
      })
    }
  }

  return (
    <Card>
      <Stack>
        {DAYS_OF_WEEK.map((day) => {
          const error = errors[day]
          const hours = watch(day)
          const timeSlotOptions = TIME_SLOTS.map((slot, index) => ({
            value: index.toString(),
            label: slot.label,
          }))

          return (
            <Box>
              <Card key={day} withBorder>
                <Card.Section>
                  <Controller
                    name={`${day}.is_working_day`}
                    control={control}
                    render={({ field }) => (
                      <Group
                        justify="space-between"
                        bg={field.value ? 'orange.0' : 'gray.0'}
                        p="8 16"
                        style={{ borderBottom: '1px solid #EAECF0' }}
                      >
                        <Stack>
                          <Text fw={500} style={{ minWidth: 100 }}>
                            {day}
                          </Text>
                        </Stack>
                        <Switch
                          label="Available"
                          checked={field.value}
                          onChange={(event) => {
                            field.onChange(event.currentTarget.checked)
                            handleWorkingDayToggle(day, event.currentTarget.checked)
                          }}
                        />
                      </Group>
                    )}
                  />
                </Card.Section>
                <Card.Section>
                  <Group p="0 16" gap={8}>
                    <Group p="16 0" flex={1} style={{ borderRight: '1px solid #EAECF0' }}>
                      <Text size="sm" c="dimmed">
                        Open
                      </Text>
                      <Controller
                        name={`${day}.open`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            // Use empty placeholder if working_day is T, bc the placeholder looks real if there is an error
                            placeholder={
                              hours?.is_working_day
                                ? undefined
                                : getTimeSlotValue(hoursFromBackend[day]?.open) || undefined
                            }
                            data={timeSlotOptions}
                            value={field.value !== null && field.value !== undefined ? field.value.toString() : null}
                            onChange={(value) => {
                              field.onChange(value ? parseInt(value) : null)
                              trigger(day)
                            }}
                            disabled={!hours?.is_working_day}
                            error={error && hours?.is_working_day}
                            style={{ minWidth: 100 }}
                          />
                        )}
                      />
                    </Group>
                    <Group p="16 0" flex={1}>
                      <Text size="sm" c="dimmed">
                        Close
                      </Text>
                      <Controller
                        name={`${day}.close`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            // NOTE: Use empty placeholder if working_day is T, bc the placeholder looks real if there is an error
                            placeholder={
                              hours?.is_working_day
                                ? undefined
                                : getTimeSlotValue(hoursFromBackend[day]?.close) || undefined
                            }
                            data={timeSlotOptions}
                            value={field.value !== null && field.value !== undefined ? field.value.toString() : null}
                            onChange={(value) => {
                              field.onChange(value ? parseInt(value) : null)
                              trigger(day)
                            }}
                            disabled={!hours?.is_working_day}
                            error={error && hours?.is_working_day}
                            style={{ minWidth: 100 }}
                          />
                        )}
                      />
                    </Group>
                  </Group>
                </Card.Section>
              </Card>

              {error && (
                <Text size="xs" c="red" p="8 16 0 16">
                  {error.message}
                </Text>
              )}
            </Box>
          )
        })}

        <Group justify="flex-end">
          <Button onClick={handleSubmit(onSubmit)} loading={updateBranchMutation.isPending} disabled={!isDirty}>
            Save changes
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}
